import { z } from 'zod';

/** ULID (docs/01 §6): 26 chars, Crockford base32, first char 0-7. */
export const Ulid = z
  .string()
  .regex(/^[0-7][0-9A-HJKMNP-TV-Z]{25}$/, 'Expected a ULID')
  .describe('ULID');
export type Ulid = z.infer<typeof Ulid>;

/**
 * Id of a catalog entity (song, artist, playlist, hero, home row). CMS-created rows get ULIDs
 * (docs/01 §6); the seed uses fixed slugs such as `song_100010` (docs/02 §1) so the same
 * contract accepts both: 1–64 chars of `[A-Za-z0-9_-]`.
 */
export const EntityId = z
  .string()
  .regex(/^[A-Za-z0-9_-]{1,64}$/, 'Expected an entity id')
  .describe('EntityId');
export type EntityId = z.infer<typeof EntityId>;

/** Firebase uid (stored as-is in `users.id`). Opaque, non-empty. */
export const Uid = z.string().min(1).max(128);
export type Uid = z.infer<typeof Uid>;

/** UTC ISO-8601 timestamp as used in every API payload (docs/01 §6). */
export const IsoDate = z.string().datetime({ offset: true }).describe('ISO-8601');
export type IsoDate = z.infer<typeof IsoDate>;

/** Epoch milliseconds, the RTDB/D1 representation of time. */
export const EpochMs = z.number().int().nonnegative();
export type EpochMs = z.infer<typeof EpochMs>;

export const Locales = ['en', 'es'] as const;
export const Locale = z.enum(Locales);
export type Locale = z.infer<typeof Locale>;
export const DEFAULT_LOCALE: Locale = 'es';

export const Tiers = ['standard', 'recent', 'vip'] as const;
export const Tier = z.enum(Tiers);
export type Tier = z.infer<typeof Tier>;

/** Tone rendition name: "0", "+1", "-2" … (single digit, optional sign). */
export const ToneName = z
  .string()
  .regex(/^[+-]?\d$/, 'Expected a tone name like "0", "+1" or "-2"');
export type ToneName = z.infer<typeof ToneName>;
export const DEFAULT_TONE: ToneName = '0';
/**
 * The tone chips every client offers (docs/04 §7.5). They are shown whatever the song's
 * `tones` array holds, and the API accepts every one of them: a chip the catalogue does not
 * declare (or whose derived audio is not ready) falls back to the song's default tone rather
 * than failing (docs/07 §4, decision 2026-09-14). `tone_not_available` is left for a tone
 * outside this list that the song does not declare either.
 */
export const TONE_CHOICES: readonly ToneName[] = ['-2', '-1', '0', '+1', '+2'];

/**
 * The tone names a request may carry for a song declaring `declared`: the five chips plus
 * whatever the catalogue holds for that song. Anything else is `400 tone_not_available`.
 */
export function acceptedTones(declared: readonly string[]): ToneName[] {
  return [...new Set<string>([...TONE_CHOICES, ...declared])];
}

/** Editorial text in both product languages. */
export const I18nText = z.object({ en: z.string(), es: z.string() });
export type I18nText = z.infer<typeof I18nText>;

/** Picks the localized string, falling back to the other language, then to ''. */
export function pickI18n(text: I18nText | null | undefined, locale: Locale): string {
  if (!text) return '';
  return text[locale] || text[locale === 'en' ? 'es' : 'en'] || '';
}

/** Alphabet for user codes, join codes and vouchers (docs/02 §1, 07 §3). */
export const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const codeRegex = /^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{6}$/;

/** 6-char TV pairing code shown on screen. Input is trimmed and upper-cased. */
export const UserCode = z
  .string()
  .transform((s) => s.trim().toUpperCase())
  .pipe(z.string().regex(codeRegex, 'Expected a 6-character code'));
export type UserCode = z.infer<typeof UserCode>;

/** 6-char session join code (same alphabet). */
export const JoinCode = UserCode;
export type JoinCode = z.infer<typeof JoinCode>;

/** 43-char base64url device code (RFC 8628 `device_code` analogue). */
export const DeviceCode = z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'Expected a device code');
export type DeviceCode = z.infer<typeof DeviceCode>;

/**
 * Voucher code `PREFIX-XXXX-XXXX`; accepted case-insensitively and with or without hyphens/spaces, stored uppercase with hyphens.
 *
 * The prefix is whatever the batch was generated with (`VoucherPrefix`, 2–5 letters, docs/07 §3) —
 * `AKP` is only the default. Hard-coding `AKP-` here made every batch issued from the CMS with a
 * custom prefix unredeemable (QA 2026-09-14 API-33 / MOB-14).
 */
export const VOUCHER_CODE_REGEX =
  /^[A-Z]{2,5}-[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{4}-[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{4}$/;

/**
 * Any way a person may type a voucher: `akp-abcd-2345`, `AKP ABCD 2345`, `AKPABCD2345`, with
 * en/em dashes, underscores or stray punctuation. Everything that is not a letter or a digit is
 * dropped, the rest is upper-cased, and when what is left is `PREFIX` + 8 code characters it is
 * written back as the canonical `PREFIX-XXXX-XXXX` the grants are stored under.
 *
 * Splitting needs no separators: the code body is always the LAST 8 characters, so whatever
 * letters come before them (2–5) are the prefix. Anything that does not fit is returned
 * stripped but unshaped, so the regex below refuses it with the same error as before.
 */
export function canonicalVoucher(raw: string): string {
  // Typed as three groups (`AKP-ABCD-2345`, `AKP ABCD 2345`, mixed): take them as written, so a
  // short or long group stays an error instead of being re-cut into a different, valid code.
  const groups = raw.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  if (groups.length === 3) return groups.join('-').toUpperCase();
  // No separators, or some other split (`AKPABCD2345`, `AKPABCD 2345`): the body is always the
  // last 8 characters, so everything before them is the prefix.
  const flat = groups.join('').toUpperCase();
  const prefix = flat.slice(0, -8);
  const body = flat.slice(-8);
  const shaped = `${prefix}-${body.slice(0, 4)}-${body.slice(4)}`;
  return VOUCHER_CODE_REGEX.test(shaped) ? shaped : flat;
}

export const VoucherCode = z
  .string()
  .transform(canonicalVoucher)
  .pipe(z.string().regex(VOUCHER_CODE_REGEX, 'Expected a voucher like AKP-XXXX-XXXX'));
export type VoucherCode = z.infer<typeof VoucherCode>;

export const Url = z.string().url();
export type Url = z.infer<typeof Url>;

export const Email = z.string().trim().toLowerCase().email().max(254);
export type Email = z.infer<typeof Email>;

/** Device platform (D1 `devices.platform`). */
export const Platform = z.enum(['android_tv', 'web']);
export type Platform = z.infer<typeof Platform>;

/** CMS role (D1 `users.role`). */
export const Roles = ['member', 'viewer', 'editor', 'admin'] as const;
export const Role = z.enum(Roles);
export type Role = z.infer<typeof Role>;

/** Role inside a session. */
export const SessionRole = z.enum(['host', 'guest']);
export type SessionRole = z.infer<typeof SessionRole>;

/**
 * Why a session stopped. Lives here because both the D1 row (`sessions.ts`) and the live RTDB
 * mirror (`rtdb.ts` `SessionMeta`) carry it, and `sessions.ts` already imports `rtdb.ts`.
 */
export const ClosedReason = z.enum(['expired', 'host_logout', 'admin', 'tv_unpaired']);
export type ClosedReason = z.infer<typeof ClosedReason>;

/**
 * Which client issued a queue mutation (D1 `session_songs.added_via`, RTDB `addedVia`). `web` is the
 * standalone web (docs/13 §7.2); the API only stores it in a web session, so a stick never reads it.
 */
export const Via = z.enum(['tv', 'mobile', 'web']);
export type Via = z.infer<typeof Via>;

/** Generic acknowledgement body for mutations with nothing else to return. */
export const ApiOk = z.object({ ok: z.literal(true) });
export type ApiOk = z.infer<typeof ApiOk>;

/** Server pagination (docs/06 §4: 50/page). */
export const PageQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  sort: z.string().max(64).optional(),
  dir: z.enum(['asc', 'desc']).optional(),
});
export type PageQuery = z.infer<typeof PageQuery>;
export type PageQueryInput = z.input<typeof PageQuery>;

export function Page<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    total: z.number().int().nonnegative(),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
  });
}
export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** Path params `{ id }` for every `/:id` route (ULID or seed slug, see `EntityId`). */
/** The fields every home hero has, on the TV/mobile contract and on the web one. */
export const HeroBaseFields = {
  id: EntityId,
  name: z.string(),
  posterTvUrl: z.string(),
  posterMobileUrl: z.string(),
};

export const IdParams = z.object({ id: EntityId });
export type IdParams = z.infer<typeof IdParams>;

/** Trimmed, bounded free-text query. */
export const SearchText = z.string().trim().min(1).max(100);
export type SearchText = z.infer<typeof SearchText>;
